import type { AIConnector } from "@p0security/shared/types/ai-inventory";
import { Background, Controls, type Node, ReactFlow } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Spin } from "antd";
import { useCallback, useMemo, useState } from "react";

import { EmptyState } from "../EmptyState.tsx";
import type { ManagedMcpServer } from "../hooks/useManagedMcpServers.ts";
import { useToolsForServers } from "../hooks/useToolsForServers.ts";
import { buildGraph } from "./build.ts";
import { layoutGraph } from "./layout.ts";
import { NODE_TYPES } from "./nodes/index.ts";
import type { AgentGraphData } from "./types.ts";

// Pixel heights to reserve in the dagre layout for risk nodes. Risk
// nodes always render a row of standards tags at the bottom, so the
// collapsed height is taller than the default node. Expanded adds the
// description block on top of that.
const COLLAPSED_RISK_HEIGHT = 96;
const EXPANDED_RISK_HEIGHT = 200;

export type SidecarGraphProps = {
  data: AgentGraphData;
  onConnectorClick?: (connector: AIConnector) => void;
  onServerClick?: (server: ManagedMcpServer) => void;
  onSecureConnector?: (connector: AIConnector) => void;
  /** When provided, fills the parent height; otherwise uses a fixed pixel height. */
  fillHeight?: boolean;
  height?: number;
  /**
   * True while upstream session/server data the graph depends on is still
   * fetching. Shown as a centered spinner instead of the empty state so the
   * sidecar doesn't briefly flash "No connectors discovered" before traffic-
   * derived nodes (e.g. local-agents) populate.
   */
  loading?: boolean;
};

export const SidecarGraph: React.FC<SidecarGraphProps> = ({
  data,
  onConnectorClick,
  onServerClick,
  onSecureConnector,
  fillHeight = false,
  height = 480,
  loading = false,
}) => {
  const { toolsByServer } = useToolsForServers(data.mcpServers);

  // Tracks which risk nodes the user has expanded. Lifted here (rather than
  // local to RiskNode) so dagre can re-run layout with the larger expanded
  // height — neighbors get pushed out of the way instead of being covered.
  const [expandedRiskIds, setExpandedRiskIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const toggleRisk = useCallback((nodeId: string) => {
    setExpandedRiskIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const { nodes, edges } = useMemo(() => {
    const raw = buildGraph(data, new Set<string>());
    const sizeOverrides = new Map<string, { height: number }>();
    for (const node of raw.nodes) {
      if (node.type !== "risk") continue;
      sizeOverrides.set(node.id, {
        height: expandedRiskIds.has(node.id)
          ? EXPANDED_RISK_HEIGHT
          : COLLAPSED_RISK_HEIGHT,
      });
    }
    const laid = layoutGraph(raw.nodes, raw.edges, sizeOverrides);
    // Overlay callbacks + live tool counts derived from access logs.
    const decorated = laid.map((n) => {
      if (n.type === "connector") {
        return { ...n, data: { ...n.data, onSecure: onSecureConnector } };
      }
      if (n.type === "mcpServer") {
        const serverId = (n.data as { server: { id: string } }).server.id;
        const toolCount = toolsByServer.get(serverId)?.size ?? 0;
        return { ...n, data: { ...n.data, toolCount } };
      }
      if (n.type === "risk") {
        return {
          ...n,
          data: {
            ...n.data,
            expanded: expandedRiskIds.has(n.id),
            onToggle: () => toggleRisk(n.id),
          },
        };
      }
      return n;
    });
    return { nodes: decorated, edges: raw.edges };
  }, [data, onSecureConnector, toolsByServer, expandedRiskIds, toggleRisk]);

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node.type === "connector") {
        const nodeData = node.data as { connector: AIConnector };
        onConnectorClick?.(nodeData.connector);
        return;
      }
      if (node.type === "mcpServer") {
        const nodeData = node.data as { server: ManagedMcpServer };
        onServerClick?.(nodeData.server);
      }
    },
    [onConnectorClick, onServerClick]
  );

  const frameStyle: React.CSSProperties = fillHeight
    ? { width: "100%", flex: 1, minHeight: 320 }
    : { width: "100%", height };

  // Local-agents have no connector records; their graph is driven by
  // log-observed MCP server traffic. Only show the empty state when there's
  // genuinely nothing to render — neither connectors nor observed servers.
  if (data.connectors.length === 0 && data.mcpServers.length === 0) {
    return (
      <div
        style={{
          ...(fillHeight
            ? frameStyle
            : { width: "100%", height: Math.max(180, height / 2) }),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading ? (
          <Spin />
        ) : (
          <EmptyState
            title="No connectors discovered for this agent"
            hint="Once this agent uses a connector it will show up here with live session data."
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        ...frameStyle,
        animation: "p0-fade-in 320ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.08, maxZoom: 1.2, minZoom: 0.5 }}
        minZoom={0.3}
        maxZoom={2}
        onNodeClick={handleNodeClick}
        nodesDraggable={false}
        nodesConnectable={false}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};
